import { computed, ref, type Ref } from 'vue'
import { parseScoreSchema, replayScoreEvents } from '~/utils/scoring'
import type { LiveScoreState, ScoreEvent, ScoreSchema } from '~/utils/scoring'

// Local-only scoring driver for PREVIEWING a scoring system: same interface shape as
// useLiveScoring so LiveCounter renders identically, but nothing ever touches the server —
// no session, no sync, no fixture. Used by the admin "Preview score input" dialog.
export const useScoringPreview = () => {
    const schema: Ref<ScoreSchema | null> = ref(null)
    const state: Ref<LiveScoreState | null> = ref(null)
    const syncState = ref<'idle'>('idle')
    const pendingCount = computed(() => 0)
    const finalError = ref<string | null>(null)
    const conflict = ref(null)

    let events: ScoreEvent[] = []
    let nextSequence = 0

    const rebuild = () => {
        if (schema.value) state.value = replayScoreEvents(schema.value, events)
    }

    const load = (schemaJson: string, paramValuesJson?: string | null) => {
        schema.value = parseScoreSchema(schemaJson, paramValuesJson)
        events = []
        nextSequence = 0
        rebuild()
    }

    const reset = () => {
        events = []
        nextSequence = 0
        rebuild()
    }

    const record = (type: string, payload?: Record<string, unknown>) => {
        const evt: ScoreEvent = {
            clientEventId: `preview-${++nextSequence}`,
            sequence: nextSequence,
            type,
            payload: payload ?? null,
            occurredAt: new Date().toISOString(),
        }
        events.push(evt)
        rebuild()
        return evt
    }

    const undo = () => {
        const undoneIds = new Set(
            events.filter((e) => e.type === 'undo' && e.undoesClientEventId).map((e) => e.undoesClientEventId),
        )
        const target = [...events]
            .reverse()
            .find((e) => e.type !== 'undo' && e.type !== 'setup' && !undoneIds.has(e.clientEventId))
        if (!target) return
        const evt = record('undo')
        evt.undoesClientEventId = target.clientEventId
        rebuild()
    }

    return {
        // state (matches useLiveScoring's surface used by LiveCounter)
        schema,
        state,
        syncState,
        pendingCount,
        finalError,
        conflict,
        // preview lifecycle
        load,
        reset,
        // recording
        record,
        setup: (side: string) => record('setup', { side }),
        rally: (wonBy: string) => record('rally', { wonBy }),
        point: (side: string, value = 1) => record('point', { side, value }),
        visit: (side: string, total: number, darts = 3) => record('visit', { side, total, darts }),
        pot: (side: string, value: number) => record('pot', { side, value }),
        foul: (side: string, value: number) => record('foul', { side, value }),
        miss: (side: string) => record('miss', { side }),
        endScore: (side: string | null, value: number) => record('endScore', { side, value }),
        unitEnd: (side?: string | null) => record('unitEnd', side ? { side } : {}),
        annotate: (key: string, payload?: Record<string, unknown>) => record('event', { key, ...payload }),
        undo,
        abandon: async () => reset(),
    }
}
